import { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { slugify } from 'Utils/Functions';
import axiosInstance from 'Utils/Interceptor';
import { setUserLocation } from '../Redux/features/LocationSlice';

const DEFAULT_CITY = 'Ahmedabad';
const DEFAULT_CITY_SLUG = slugify(DEFAULT_CITY);

const HomeRedirectCity = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { city: currentCityInURL } = useParams();
  const [isCheckingCity, setIsCheckingCity] = useState(false);

  const locationData = useSelector((state) => state.location);
  const cityFromStoreRaw = locationData?.cityName;
  const cityFromStore = useMemo(
    () => slugify(cityFromStoreRaw),
    [cityFromStoreRaw]
  );

  const getCityCoordinates = async (cityName, stateName) => {
    // Implement your actual geocoding logic here
    return { lat: 0, lng: 0 }; // placeholder
  };

  const checkCity = async (citySlug) => {
    setIsCheckingCity(true);
    try {
      const { data } = await axiosInstance.get(
        `/api/user/citydetail/${citySlug}`
      );

      if (data?.status) {
        const { lat, lng } = await getCityCoordinates(
          data.data.city_name,
          data.data.state_name
        );

        dispatch(
          setUserLocation({
            cityId: data.data.city_id,
            cityName: data.data.city_name,
            isLocation: true,
            stateId: data.data.state_id,
            stateName: data.data.state_name,
            lat,
            lng
          })
        );
      } else {
        // City not found - fallback to Ahmedabad
        const { data: defaultData } = await axiosInstance.get(
          `/api/user/citydetail/${DEFAULT_CITY_SLUG}`
        );

        if (defaultData?.status) {
          const { lat, lng } = await getCityCoordinates(
            defaultData.data.city_name,
            defaultData.data.state_name
          );

          dispatch(
            setUserLocation({
              cityId: defaultData.data.city_id,
              cityName: defaultData.data.city_name,
              isLocation: true,
              stateId: defaultData.data.state_id,
              stateName: defaultData.data.state_name,
              lat,
              lng
            })
          );
          // Redirect to default city
          redirectToCity(DEFAULT_CITY_SLUG);
        } else {
          // Even default city failed - set location as invalid
          dispatch(
            setUserLocation({
              ...locationData,
              isLocation: false
            })
          );
        }
      }
    } catch (error) {
      console.error('Error checking city:', error);
      dispatch(
        setUserLocation({
          ...locationData,
          isLocation: false
        })
      );
      // Fallback to default city on error
      redirectToCity(DEFAULT_CITY_SLUG);
    } finally {
      setIsCheckingCity(false);
    }
  };

  const redirectToCity = (citySlug) => {
    const currentPath = location.pathname;
    const cityPattern = currentCityInURL ? `/${currentCityInURL}` : '';

    const updatedPath = cityPattern
      ? currentPath.replace(cityPattern, `/${citySlug}`)
      : `/${citySlug}${currentPath}`;

    if (updatedPath !== currentPath) {
      navigate(updatedPath + location.search, { replace: true });
    }
  };

  useEffect(() => {
    // Check city only if URL city is different from store and not already checking
    if (
      currentCityInURL &&
      currentCityInURL !== cityFromStore &&
      !isCheckingCity
    ) {
      checkCity(currentCityInURL);
    }
  }, [currentCityInURL]);

  useEffect(() => {
    // Redirect if city in store is different from URL and not checking
    if (
      cityFromStore &&
      currentCityInURL !== cityFromStore &&
      !isCheckingCity
    ) {
      redirectToCity(cityFromStore);
    }
  }, [cityFromStore, currentCityInURL, isCheckingCity]);

  // if (isCheckingCity) return <div>Loading location...</div>;
  // if (!cityFromStore) return <div>Redirecting to {DEFAULT_CITY}...</div>;

  return <>{children}</>;
};

export default HomeRedirectCity;
